/*
 * @Author: Ryan
 * @Date: 2026-01-16
 * @Description: 结构化数据生成工具函数
 */

/** 默认社交媒体链接 */
const DEFAULT_SAME_AS = [
  'https://www.instagram.com/falcocutai_official',
  'https://www.tiktok.com/@falcocutai',
  'https://x.com/falcocut_ai',
];

/** 默认支持的语言 - 目前项目只支持英文 */
const DEFAULT_IN_LANGUAGE = ['en'];

/** 默认操作系统 - 目前只支持 Web */
const DEFAULT_OPERATING_SYSTEM = 'Web';

/**
 * SoftwareApplication 结构化数据配置
 */
export interface SoftwareAppSchemaOptions {
  /** 应用名称 */
  name: string;
  /** 应用描述 */
  description: string;
  /** 应用 URL */
  url: string;
  /** 应用类别，默认 MultimediaApplication */
  applicationCategory?: string;
  /** 操作系统，默认包含多平台 */
  operatingSystem?: string;
  /** 价格，默认 0 */
  price?: string;
  /** 货币，默认 USD */
  priceCurrency?: string;
  /** 评分值 */
  ratingValue?: string;
  /** 评分数量 */
  ratingCount?: string;
  /** 评论数量 */
  reviewCount?: string;
  /** 功能列表 */
  featureList?: string[];
  /** 支持的语言列表 */
  inLanguage?: string[];
  /** 社交媒体链接 */
  sameAs?: string[];
  /** 可用性，默认 InStock */
  availability?: string;
}

/**
 * 创建 SoftwareApplication 结构化数据
 * @param options - 配置选项
 * @returns JSON-LD 结构化数据对象
 */
export const createSoftwareAppSchema = (options: SoftwareAppSchemaOptions) => {
  const {
    name,
    description,
    url,
    applicationCategory = 'MultimediaApplication',
    operatingSystem = DEFAULT_OPERATING_SYSTEM,
    price = '0',
    priceCurrency = 'USD',
    ratingValue,
    ratingCount,
    reviewCount,
    featureList,
    inLanguage = DEFAULT_IN_LANGUAGE,
    sameAs = DEFAULT_SAME_AS,
    availability = 'InStock',
  } = options;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    applicationCategory,
    operatingSystem,
    url,
    inLanguage,
    offers: {
      '@type': 'Offer',
      price,
      priceCurrency,
      availability: `https://schema.org/${availability}`,
    },
    sameAs,
  };

  // 添加评分信息（如果提供）
  if (ratingValue && ratingCount) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue,
      ratingCount,
      reviewCount: reviewCount || ratingCount,
    };
  }

  // 添加功能列表（如果提供）
  if (featureList && featureList.length > 0) {
    schema.featureList = featureList;
  }

  return schema;
};

/**
 * WebPage 结构化数据配置
 */
export interface WebPageSchemaOptions {
  /** 页面名称 */
  name: string;
  /** 页面描述 */
  description: string;
  /** 页面 URL */
  url: string;
  /** 发布日期 (YYYY-MM-DD) */
  datePublished?: string;
  /** 修改日期 (YYYY-MM-DD) */
  dateModified?: string;
}

/**
 * 创建 WebPage 结构化数据
 * @param options - 配置选项
 * @returns JSON-LD 结构化数据对象
 */
export const createWebPageSchema = (options: WebPageSchemaOptions) => {
  const { name, description, url, datePublished, dateModified } = options;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url,
  };

  if (datePublished) {
    schema.datePublished = `${datePublished}T00:00:00+00:00`;
  }

  if (dateModified) {
    schema.dateModified = `${dateModified}T00:00:00+00:00`;
  }

  return schema;
};

/**
 * FAQPage 结构化数据配置
 */
export interface FAQSchemaOptions {
  /** FAQ 列表 */
  faqs: Array<{
    question: string;
    answer: string;
  }>;
}

/**
 * 创建 FAQPage 结构化数据
 * @param options - 配置选项
 * @returns JSON-LD 结构化数据对象
 */
export const createFAQSchema = (options: FAQSchemaOptions) => {
  const { faqs } = options;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
};

/**
 * Organization 结构化数据配置
 */
export interface OrganizationSchemaOptions {
  /** 组织名称 */
  name: string;
  /** 组织 URL */
  url: string;
  /** Logo URL */
  logo?: string;
  /** 社交媒体链接 */
  sameAs?: string[];
}

/**
 * 创建 Organization 结构化数据
 * @param options - 配置选项
 * @returns JSON-LD 结构化数据对象
 */
export const createOrganizationSchema = (options: OrganizationSchemaOptions) => {
  const { name, url, logo, sameAs = DEFAULT_SAME_AS } = options;

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    sameAs,
  };

  if (logo) {
    schema.logo = logo;
  }

  return schema;
};

/**
 * BreadcrumbList 面包屑配置
 */
export interface BreadcrumbItem {
  /** 面包屑名称 */
  name: string;
  /** 面包屑 URL */
  url: string;
}

export interface BreadcrumbSchemaOptions {
  /** 面包屑列表 */
  items: BreadcrumbItem[];
}

/**
 * 创建 BreadcrumbList 结构化数据
 * @param options - 配置选项
 * @returns JSON-LD 结构化数据对象
 */
export const createBreadcrumbSchema = (options: BreadcrumbSchemaOptions) => {
  const { items } = options;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * HowTo 结构化数据配置
 */
export interface HowToSchemaOptions {
  /** 教程名称 */
  name: string;
  /** 步骤列表 */
  steps: Array<{
    name: string;
    text: string;
  }>;
}

/**
 * 创建 HowTo 结构化数据
 * @param options - 配置选项
 * @returns JSON-LD 结构化数据对象
 */
export const createHowToSchema = (options: HowToSchemaOptions) => {
  const { name, steps } = options;

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };
};
